// Props: benches, lamps, bins, fountains, flowers, signs — cheap 1-cell decorations
// with a comfort radius. Comfort per path cell recomputes only on build/demolish.
// Props MAY be placed on paths (bench on the boulevard) as well as beside them.
import * as THREE from 'three';
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { softTileTexture, rampColor, OVERLAY, pulse } from './overlayStyle.js';
import { bakeToSingleMesh } from '../core/meshUtils.js';

export const PROPS = {
  bench:    { label: 'Bench',      price: 150, icon: '🪑', comfort: 25, radius: 15 },
  lamp:     { label: 'Lamp Post',  price: 200, icon: '🏮', comfort: 15, radius: 12 },
  bin:      { label: 'Trash Bin',  price: 100, icon: '🗑️', comfort: 10, radius: 15 },
  fountain: { label: 'Fountain',   price: 800, icon: '⛲', comfort: 40, radius: 8 },
  flowers:  { label: 'Flower Bed', price: 120, icon: '🌷', comfort: 12, radius: 4 },
  sign:     { label: 'Sign Post',  price: 80,  icon: '🪧', comfort: 5,  radius: 4 },
  // Enrichment — placed INSIDE enclosures, raise animal wellbeing (see PropSystem.enrichmentBonus)
  climblog: { label: 'Climbing Log', price: 300, icon: '🪵', enclosure: true, enrich: 8,  radius: 4 },
  rockpile: { label: 'Rock Pile',    price: 250, icon: '🪨', enclosure: true, enrich: 6,  radius: 4 },
  pool:     { label: 'Water Pool',   price: 500, icon: '💧', enclosure: true, enrich: 10, radius: 5 },
  ball:     { label: 'Play Ball',    price: 150, icon: '⚽', enclosure: true, enrich: 5,  radius: 3 },
  trough:   { label: 'Drinking Trough', price: 400, icon: '🫧', enclosure: true, enrich: 10, radius: 5 },
  barn:     { label: 'Wooden Barn',  price: 1200, icon: '🏚️', enclosure: true, enrich: 16, radius: 7 },
  doghouse: { label: 'Dog House',    price: 200, icon: '🐶', enclosure: true, enrich: 6,  radius: 3 },
  hedge:    { label: 'Privacy Hedge', price: 350, icon: '🌳', enclosure: true, enrich: 3, shelter: true, radius: 4 },
};

const MODEL_CACHE = new Map();

function buildProp(type, cellSize) {
  const key = type + '|' + cellSize;
  if (!MODEL_CACHE.has(key)) MODEL_CACHE.set(key, bakeToSingleMesh(rawBuild(type, cellSize)));
  return MODEL_CACHE.get(key).clone();
}

function rawBuild(type, cellSize) {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x8a6a42, roughness: 0.85 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x4a5056, roughness: 0.5, metalness: 0.6 });
  const stone = new THREE.MeshStandardMaterial({ color: 0xb8b2a6, roughness: 0.9 });
  const add = (geo, mat, x, y, z, ry = 0) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.rotation.y = ry;
    m.castShadow = true;
    g.add(m);
    return m;
  };
  switch (type) {
    case 'bench': {
      add(new THREE.BoxGeometry(1.4, 0.08, 0.45), wood, 0, 0.45, 0);
      add(new THREE.BoxGeometry(1.4, 0.4, 0.08), wood, 0, 0.75, -0.2);
      add(new THREE.BoxGeometry(0.08, 0.45, 0.4), metal, -0.6, 0.22, 0);
      add(new THREE.BoxGeometry(0.08, 0.45, 0.4), metal, 0.6, 0.22, 0);
      break;
    }
    case 'lamp': {
      add(new THREE.CylinderGeometry(0.05, 0.07, 2.6, 6), metal, 0, 1.3, 0);
      const bulb = add(new THREE.SphereGeometry(0.16, 8, 6), new THREE.MeshStandardMaterial({ color: 0xffe9b0, emissive: 0xffc94a, emissiveIntensity: 1.2 }), 0, 2.65, 0);
      bulb.castShadow = false;
      break;
    }
    case 'bin': {
      add(new THREE.CylinderGeometry(0.22, 0.18, 0.55, 8), metal, 0, 0.28, 0);
      add(new THREE.CylinderGeometry(0.24, 0.24, 0.06, 8), new THREE.MeshStandardMaterial({ color: 0x2f7a4a, roughness: 0.7 }), 0, 0.58, 0);
      break;
    }
    case 'fountain': {
      add(new THREE.CylinderGeometry(1.1, 1.2, 0.35, 12), stone, 0, 0.18, 0);
      add(new THREE.CylinderGeometry(0.85, 0.95, 0.1, 12), new THREE.MeshStandardMaterial({ color: 0x4aa8c9, roughness: 0.15, metalness: 0.1 }), 0, 0.4, 0);
      add(new THREE.CylinderGeometry(0.12, 0.16, 0.8, 8), stone, 0, 0.75, 0);
      add(new THREE.SphereGeometry(0.2, 8, 6), new THREE.MeshStandardMaterial({ color: 0x6ac0dd, roughness: 0.2 }), 0, 1.2, 0);
      break;
    }
    case 'flowers': {
      add(new THREE.CylinderGeometry(0.55, 0.6, 0.18, 8), wood, 0, 0.09, 0);
      const colors = [0xd6568a, 0xe8c93c, 0xd9762a, 0xb05ad6];
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        add(new THREE.SphereGeometry(0.09, 5, 4), new THREE.MeshStandardMaterial({ color: colors[i % 4], roughness: 0.7 }), Math.cos(a) * 0.3, 0.26, Math.sin(a) * 0.3);
      }
      break;
    }
    case 'sign': {
      add(new THREE.CylinderGeometry(0.05, 0.06, 1.4, 6), wood, 0, 0.7, 0);
      add(new THREE.BoxGeometry(0.7, 0.35, 0.06), wood, 0, 1.25, 0, 0.3);
      break;
    }
    case 'climblog': {
      const log = add(new THREE.CylinderGeometry(0.22, 0.24, 2.2, 8), wood, 0, 0.5, 0);
      log.rotation.z = Math.PI / 2; log.rotation.y = 0.3;
      add(new THREE.CylinderGeometry(0.1, 0.12, 0.8, 6), wood, 0.7, 0.3, 0.2, 0.6);
      add(new THREE.CylinderGeometry(0.1, 0.12, 0.7, 6), wood, -0.6, 0.28, -0.2, -0.5);
      break;
    }
    case 'rockpile': {
      add(new THREE.IcosahedronGeometry(0.5, 0), stone, 0, 0.35, 0);
      add(new THREE.IcosahedronGeometry(0.34, 0), stone, 0.55, 0.24, 0.2);
      add(new THREE.IcosahedronGeometry(0.28, 0), stone, -0.4, 0.2, -0.3);
      break;
    }
    case 'pool': {
      add(new THREE.CylinderGeometry(1.0, 1.05, 0.18, 14), stone, 0, 0.09, 0);
      add(new THREE.CylinderGeometry(0.85, 0.85, 0.08, 14), new THREE.MeshStandardMaterial({ color: 0x4aa8c9, roughness: 0.15, metalness: 0.1, transparent: true, opacity: 0.9 }), 0, 0.2, 0);
      break;
    }
    case 'ball': {
      add(new THREE.SphereGeometry(0.35, 12, 10), new THREE.MeshStandardMaterial({ color: 0xe8563c, roughness: 0.5 }), 0, 0.35, 0);
      break;
    }
    case 'trough': {
      // long low water trough (cow-style)
      add(new THREE.BoxGeometry(3.4, 0.5, 0.9), wood, 0, 0.25, 0);
      add(new THREE.BoxGeometry(3.1, 0.34, 0.65), new THREE.MeshStandardMaterial({ color: 0x4aa8c9, roughness: 0.15, metalness: 0.1 }), 0, 0.36, 0);
      for (const sx of [-1.6, 1.6]) add(new THREE.BoxGeometry(0.16, 0.55, 0.9), wood, sx, 0.2, 0);
      break;
    }
    case 'barn': {
      // wooden shed shelter (~2x4 cells)
      const W = 7.2, D = 3.4;
      add(new THREE.BoxGeometry(W, 2.0, D), new THREE.MeshStandardMaterial({ color: 0x9a6a42, roughness: 0.9 }), 0, 1.0, 0);
      // gable roof (two slanted boxes)
      const roofMat = new THREE.MeshStandardMaterial({ color: 0x6a4326, roughness: 0.85 });
      const r1 = add(new THREE.BoxGeometry(W + 0.4, 0.18, D * 0.72), roofMat, 0, 2.35, -D * 0.28); r1.rotation.x = -0.5;
      const r2 = add(new THREE.BoxGeometry(W + 0.4, 0.18, D * 0.72), roofMat, 0, 2.35, D * 0.28); r2.rotation.x = 0.5;
      // dark open doorway
      add(new THREE.BoxGeometry(2.0, 1.5, 0.1), new THREE.MeshStandardMaterial({ color: 0x241a12 }), 0, 0.75, D / 2 + 0.02);
      // plank lines
      for (const sx of [-W / 2 + 0.1, 0, W / 2 - 0.1]) add(new THREE.BoxGeometry(0.1, 2.0, D + 0.02), roofMat, sx, 1.0, 0);
      break;
    }
    case 'doghouse': {
      add(new THREE.BoxGeometry(1.0, 0.8, 1.0), new THREE.MeshStandardMaterial({ color: 0xb5794a, roughness: 0.85 }), 0, 0.4, 0);
      const rm = new THREE.MeshStandardMaterial({ color: 0x8a5a2e, roughness: 0.85 });
      const a1 = add(new THREE.BoxGeometry(0.85, 0.12, 0.8), rm, 0, 0.92, -0.25); a1.rotation.x = -0.6;
      const a2 = add(new THREE.BoxGeometry(0.85, 0.12, 0.8), rm, 0, 0.92, 0.25); a2.rotation.x = 0.6;
      add(new THREE.BoxGeometry(0.4, 0.5, 0.1), new THREE.MeshStandardMaterial({ color: 0x241a12 }), 0, 0.3, 0.5);
      break;
    }
    case 'hedge': {
      const leaf = new THREE.MeshStandardMaterial({ color: 0x4f7a3a, roughness: 0.95, flatShading: true });
      add(new THREE.BoxGeometry(1.9, 1.2, 0.7), leaf, 0, 0.6, 0);
      add(new THREE.IcosahedronGeometry(0.5, 0), leaf, -0.6, 1.15, 0);
      add(new THREE.IcosahedronGeometry(0.55, 0), leaf, 0, 1.2, 0);
      add(new THREE.IcosahedronGeometry(0.5, 0), leaf, 0.6, 1.15, 0);
      break;
    }
  }
  return g;
}

export class PropSystem {
  constructor(world, build) {
    this.world = world;
    this.build = build;
    this.items = [];             // { type, cx, cz, model }
    this.byCell = new Map();
    this.comfort = new Map();    // path cell "x,z" -> 0..100
    this.group = new THREE.Group();
    world.renderer.scene.add(this.group);
    this._overlayMesh = null;
    this._subs = [
      bus.on('build:propPlace', ({ type, cx, cz }) => this.tryPlace(type, cx, cz)),
      bus.on('build:pathPlaced', () => this._dirty = true),
    ];
    this._dirty = true;
  }
  dispose() { this._subs.forEach((u) => u()); }

  tryPlace(type, cx, cz) {
    const def = PROPS[type];
    const key = `${cx},${cz}`;
    if (this.byCell.has(key)) { bus.emit('ui:toast', { text: 'Cell is occupied.', kind: 'error' }); return; }
    const inEnc = this.build.enclosures.some((e) => e.cells.has(key));
    if (def.enclosure && !inEnc) { bus.emit('ui:toast', { text: 'Place enrichment inside an enclosure.', kind: 'error' }); return; }
    if (!def.enclosure && inEnc) { bus.emit('ui:toast', { text: 'Cannot build inside an enclosure.', kind: 'error' }); return; }
    const s = store.get();
    const price = s.kidsMode ? Math.ceil(def.price / 2) : def.price;
    if (s.money < price) { bus.emit('ui:toast', { text: 'Not enough cash!', kind: 'error' }); return; }
    store.set({ money: s.money - price });
    this._add(type, cx, cz);
    this._dirty = true;
  }

  _add(type, cx, cz) {
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const model = buildProp(type, t.cellSize);
    const wx = (cx + 0.5) * t.cellSize - half;
    const wz = (cz + 0.5) * t.cellSize - half;
    model.position.set(wx, t.heightAtWorld(wx, wz), wz);
    model.rotation.y = Math.random() * Math.PI * 2;
    this.group.add(model);
    const item = { type, cx, cz, model };
    this.items.push(item);
    this.byCell.set(`${cx},${cz}`, item);
  }

  restore(type, cx, cz) { this._add(type, cx, cz); this._dirty = true; }

  // Distance LOD: props are small decorations, so hide them entirely beyond ~60m (throttled, squared
  // distance). Three.js already frustum-culls off-screen meshes; this drops distant-but-in-view ones,
  // cutting draw calls hard on a prop-heavy zoo. Gameplay effects (comfort/enrichment) are unaffected.
  updateLOD(cam) {
    this._lodF = (this._lodF || 0) + 1;
    if (this._lodF % 15 !== 0) return;
    const cx = cam.position.x, cz = cam.position.z, MAX2 = 60 * 60;
    for (const it of this.items) {
      const m = it.model; if (!m) continue;
      const dx = m.position.x - cx, dz = m.position.z - cz;
      const vis = (dx * dx + dz * dz) <= MAX2;
      if (m.visible !== vis) m.visible = vis;
    }
  }

  removeAt(cx, cz) {
    const key = `${cx},${cz}`;
    const item = this.byCell.get(key);
    if (!item) return 0;
    this.group.remove(item.model);
    this.items.splice(this.items.indexOf(item), 1);
    this.byCell.delete(key);
    this._dirty = true;
    return Math.round(PROPS[item.type].price * 0.4);
  }

  // Comfort per path cell: sum of prop contributions with linear falloff, capped 100.
  recompute() {
    this.comfort.clear();
    if (!this.items.length) { this._dirty = false; return; }
    for (const key of this.build.paths) {
      const [cx, cz] = key.split(',').map(Number);
      let c = 0;
      for (const item of this.items) {
        const def = PROPS[item.type];
        const d = Math.abs(item.cx - cx) + Math.abs(item.cz - cz);
        if (d <= def.radius) c += def.comfort * (1 - d / (def.radius + 1));
      }
      this.comfort.set(key, Math.min(100, Math.round(c)));
    }
    this._dirty = false;
  }

  comfortAt(cx, cz) {
    if (this._dirty) this.recompute();
    return this.comfort.get(`${cx},${cz}`) || 0;
  }

  // Total enrichment wellbeing bonus from enrichment props placed inside an enclosure's cells (capped).
  enrichmentBonus(encCells) {
    let b = 0;
    for (const item of this.items) {
      const def = PROPS[item.type];
      if (def.enclosure && encCells.has(`${item.cx},${item.cz}`)) b += def.enrich || 0;
    }
    return Math.min(25, b);
  }

  // True if a shelter prop (privacy hedge) sits inside these enclosure cells — blocks predator stress.
  hasShelter(encCells) {
    for (const item of this.items) {
      const def = PROPS[item.type];
      if (def.shelter && encCells.has(`${item.cx},${item.cz}`)) return true;
    }
    return false;
  }

  // Green/red comfort overlay over path cells (same InstancedMesh trick as guest flow).
  updateOverlay() {
    const on = store.get().comfortOverlay;
    if (!on) {
      if (this._overlayMesh) { this.world.renderer.scene.remove(this._overlayMesh); this._overlayMesh.geometry.dispose(); this._overlayMesh = null; }
      return;
    }
    if (this._dirty) this.recompute();
    // Per-frame breathing so the heatmap feels alive (runs even when geometry is unchanged).
    if (this._overlayMesh && !this._overlayDirty) { this._overlayMesh.material.opacity = OVERLAY.tileOpacity * pulse(0.7, 0.82, 1); return; }
    if (this._overlayMesh) { this.world.renderer.scene.remove(this._overlayMesh); this._overlayMesh.geometry.dispose(); }
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const cells = [...this.build.paths];
    if (!cells.length) return;
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2);
    const im = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({ map: softTileTexture(), transparent: true, opacity: OVERLAY.tileOpacity, depthWrite: false }), cells.length);
    const m = new THREE.Matrix4(), p = new THREE.Vector3(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
    const color = new THREE.Color();
    cells.forEach((key, i) => {
      const [cx, cz] = key.split(',').map(Number);
      const wx = (cx + 0.5) * t.cellSize - half;
      const wz = (cz + 0.5) * t.cellSize - half;
      p.set(wx, t.heightAtWorld(wx, wz) + OVERLAY.liftComfort, wz);
      sc.setScalar(t.cellSize * 1.4);   // heavy overlap → tiles blend into one continuous heat blob
      m.compose(p, q, sc);
      im.setMatrixAt(i, m);
      const c = this.comfort.get(key) || 0;
      rampColor(color, c / 100);   // low(rose) → ok(amber) → great(green)
      im.setColorAt(i, color);
    });
    im.instanceColor.needsUpdate = true;
    this.world.renderer.scene.add(im);
    this._overlayMesh = im;
    this._overlayDirty = false;
  }

  markOverlayDirty() { this._overlayDirty = true; }
}
